import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaptchaSubmission } from '../types/auth.types';

export interface TencentCaptchaConfig {
  enabled: boolean;
  captchaAppId: number;
  clientJsUrl: string;
}

const DEFAULT_TENCENT_CAPTCHA_JS_URL = 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js';

function normalizeTencentCaptchaJsUrl(url: string): string {
  const normalized = String(url || '').trim();
  switch (normalized) {
    case '':
    case 'https://turing.captcha.qcloud.com/TJCaptcha.js':
    case 'https://sg.captcha.qcloud.com/TCaptcha-global.js':
    case 'https://global.captcha.gtimg.com/TCaptcha-global.js':
    case 'https://ca.turing.captcha.qcloud.com/TCaptcha-global.js':
      return DEFAULT_TENCENT_CAPTCHA_JS_URL;
    default:
      return normalized;
  }
}

declare global {
  interface Window {
    TencentCaptcha?: new (
      mountNode: HTMLElement,
      appId: string | number,
      callback: (result: {
        ret?: number;
        ticket?: string;
        randstr?: string;
        errorCode?: number;
        errorMessage?: string;
      } | null) => void,
      options?: {
        userLanguage?: string;
      },
    ) => {
      show: () => void;
      destroy?: () => void;
    };
  }
}

let loadedScriptUrl = '';
let scriptPromise: Promise<void> | null = null;

function loadTencentCaptchaScript(url: string): Promise<void> {
  if (typeof window.TencentCaptcha === 'function' && loadedScriptUrl === url) {
    return Promise.resolve();
  }
  if (scriptPromise && loadedScriptUrl === url) {
    return scriptPromise;
  }

  loadedScriptUrl = url;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-tcaptcha-url="${url}"]`);
    if (existing && typeof window.TencentCaptcha === 'function') {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.dataset.tcaptchaUrl = url;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Tencent captcha script load failed'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function resolveTencentError(result: {
  ticket?: string;
  errorCode?: number;
  errorMessage?: string;
} | null): string {
  const ticket = String(result?.ticket || '');
  if (typeof result?.errorCode !== 'undefined') {
    return `[${result.errorCode}] ${result.errorMessage || '验证组件执行失败'}`;
  }
  if (ticket.startsWith('trerror_')) {
    return result?.errorMessage || ticket;
  }
  return '验证组件执行失败，请刷新页面重试';
}

export function useTencentCaptcha(
  config: TencentCaptchaConfig,
  onVerified: (submission: CaptchaSubmission) => void,
) {
  const onVerifiedRef = useRef(onVerified);
  const instanceRef = useRef<{
    show: () => void;
    destroy?: () => void;
  } | null>(null);
  const mountNodeRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  onVerifiedRef.current = onVerified;

  const hideOverlay = useCallback(() => {
    if (overlayRef.current) {
      overlayRef.current.style.display = 'none';
    }
  }, []);

  const ensureOverlay = useCallback(() => {
    if (overlayRef.current && mountNodeRef.current) {
      return mountNodeRef.current;
    }

    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.zIndex = '9999';
    overlay.style.display = 'none';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.background = 'rgba(15, 23, 42, 0.35)';
    overlay.style.padding = '24px';

    const panel = document.createElement('div');
    panel.style.width = 'min(420px, 100%)';
    panel.style.minHeight = '240px';
    panel.style.background = '#ffffff';
    panel.style.borderRadius = '16px';
    panel.style.boxShadow = '0 20px 60px rgba(15, 23, 42, 0.18)';
    panel.style.padding = '20px 16px 16px';
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.gap = '12px';

    const header = document.createElement('div');
    header.style.fontSize = '16px';
    header.style.fontWeight = '600';
    header.style.color = '#111827';
    header.style.textAlign = 'center';
    header.textContent = '安全验证';

    const mountNode = document.createElement('div');
    mountNode.style.width = '100%';
    mountNode.style.minHeight = '180px';

    panel.appendChild(header);
    panel.appendChild(mountNode);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    overlayRef.current = overlay;
    mountNodeRef.current = mountNode;
    return mountNode;
  }, []);

  useEffect(() => {
    if (!config.enabled) {
      setReady(false);
      setError(null);
      instanceRef.current = null;
      hideOverlay();
      return;
    }

    if (!config.captchaAppId || !config.clientJsUrl) {
      setReady(false);
      setError('验证组件配置不完整');
      return;
    }

    let cancelled = false;
    setReady(false);
    setError(null);

    const clientJsUrl = normalizeTencentCaptchaJsUrl(config.clientJsUrl);

    loadTencentCaptchaScript(clientJsUrl)
      .then(() => {
        if (cancelled) {
          return;
        }
        if (typeof window.TencentCaptcha !== 'function') {
          setError('验证组件初始化失败，请刷新页面重试');
          return;
        }

        const mountNode = ensureOverlay();

        instanceRef.current = new window.TencentCaptcha(
          mountNode,
          String(config.captchaAppId),
          (result) => {
            if (!result) {
              return;
            }
            const ticket = String(result.ticket || '');
            const randstr = String(result.randstr || '');
            if (typeof result.errorCode !== 'undefined' || ticket.startsWith('trerror_')) {
              hideOverlay();
              setError(resolveTencentError(result));
              return;
            }
            if (result.ret === 0 && ticket && randstr) {
              hideOverlay();
              setError(null);
              onVerifiedRef.current({
                captchaTicket: ticket,
                captchaRandstr: randstr,
              });
              return;
            }
            if (result.ret === 2) {
              hideOverlay();
            }
          },
          {
            userLanguage: 'zh-cn',
          },
        );
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setError('验证组件加载失败，请刷新页面重试');
        }
      });

    return () => {
      cancelled = true;
      hideOverlay();
      instanceRef.current?.destroy?.();
      instanceRef.current = null;
      overlayRef.current?.remove();
      overlayRef.current = null;
      mountNodeRef.current = null;
    };
  }, [config.captchaAppId, config.clientJsUrl, config.enabled, ensureOverlay, hideOverlay]);

  const trigger = useCallback(() => {
    setError(null);
    if (overlayRef.current) {
      overlayRef.current.style.display = 'flex';
    }
    instanceRef.current?.show();
  }, []);

  return { ready, error, trigger };
}
